/**
 * TOSS Web Application — Client Portal notification hooks
 *
 * Data layer of `/client/notifications`: the paged feed, the unread counter, and the two
 * mark-as-read writes.
 *
 * **A feed, and only a feed.** Release Р1 has no push for this audience (decision Р12, rev.
 * 2026-08-18): `/api/client/auth/fcm-token` does not exist, so nothing here — and nothing on
 * the screen — may ask the browser for notification permission. Liveness comes from
 * `useClientNotificationsRealtime` plus the badge poll.
 *
 * `API_ENDPOINTS.NOTIFICATIONS` in `types/api.types.ts` is hard-coded to `/api/lk/notifications`
 * and answers 403 for a client session. It is neither used nor retrofitted here; the portal's
 * paths live in `api/clientPortal/clientNotifications.api.ts`.
 *
 * Marking read invalidates `clientKeys.notifications.all`, which covers BOTH the list and
 * `clientKeys.notifications.unreadCount()` — the key `useClientBadges` reads for the bell and
 * the bottom-nav badge. Invalidating only the list would leave a badge claiming unread items
 * the customer has just read.
 *
 * Contract: r1-api-contract §2 «Уведомления»; DTOs in r1-client-dtos §3.4; review decisions Р1
 * (PATCH, not PUT — mirrored in the API module) and Р5 (`Task`/`Expense` → `Other`).
 */

import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { clientNotificationsApi } from '@/api/clientPortal/clientNotifications.api';
import type {
  ClientNotificationDto,
  ClientNotificationFilterRequest,
  ClientNotificationListResponse,
  ClientUnreadCountDto,
} from '@/api/clientPortal/clientPortal.types';
import { clientKeys } from '@/hooks/api/clientPortal/clientQueryKeys';
import { pagedInfiniteOptions } from '@/hooks/api/pagination';
import { useAuthStore } from '@/store/authStore';
import { UserRole } from '@/types';

/**
 * Re-exported so a screen needs one import for the whole feature. It is the SAME query the
 * portal chrome polls (`clientKeys.notifications.unreadCount()`), so header, bottom nav and
 * page share a single request.
 */
export { useClientNotificationsUnread } from '@/hooks/api/clientPortal/useClientBadges';

// ============================================================================
// Constants
// ============================================================================

/** Feed page size. */
export const CLIENT_NOTIFICATIONS_PAGE_SIZE = 20;

const NOTIFICATIONS_STALE_TIME_MS = 30_000;

// ============================================================================
// Session gate
// ============================================================================

/** Whether portal queries may run: an authenticated session whose role is `Client`. */
function useIsClientSession(): boolean {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isClient = useAuthStore((state) => state.user?.role === UserRole.Client);
  return isAuthenticated && isClient;
}

// ============================================================================
// Cache helpers
// ============================================================================

/** Flatten the loaded pages into one list, newest first (the order the server returns). */
export function flattenClientNotifications(
  data: InfiniteData<ClientNotificationListResponse> | undefined
): ClientNotificationDto[] {
  return data?.pages.flatMap((page) => page.items ?? []) ?? [];
}

/** Find a notification in whatever list pages are currently cached. */
function findCachedNotification(
  queryClient: QueryClient,
  id: string
): ClientNotificationDto | undefined {
  const caches = queryClient.getQueriesData<InfiniteData<ClientNotificationListResponse>>({
    queryKey: clientKeys.notifications.lists(),
  });

  for (const [, data] of caches) {
    for (const page of data?.pages ?? []) {
      const found = (page.items ?? []).find((item) => item.id === id);
      if (found) return found;
    }
  }
  return undefined;
}

/** Patch every cached list page in place. */
function patchCachedNotifications(
  queryClient: QueryClient,
  patch: (item: ClientNotificationDto) => ClientNotificationDto
): void {
  queryClient.setQueriesData<InfiniteData<ClientNotificationListResponse>>(
    { queryKey: clientKeys.notifications.lists() },
    (current) =>
      current
        ? {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              items: (page.items ?? []).map(patch),
            })),
          }
        : current
  );
}

/** Move the cached unread counter, floored at zero. */
function adjustCachedUnreadCount(queryClient: QueryClient, next: (count: number) => number): void {
  queryClient.setQueryData<ClientUnreadCountDto>(clientKeys.notifications.unreadCount(), (current) =>
    current ? { count: Math.max(0, next(current.count)) } : current
  );
}

// ============================================================================
// Reads
// ============================================================================

export interface UseClientNotificationsOptions {
  /** `false` narrows the feed to unread items (the «Только непрочитанные» chip). */
  isRead?: boolean;
  /**
   * Extra gate, ANDed with the session check. It never widens the gate: a non-client session
   * runs nothing regardless.
   */
  enabled?: boolean;
}

/**
 * The paged notification feed, newest first.
 *
 * Page-number paged (unlike the chat, which is cursor paged) — `PaginatedList<T>` all the way,
 * so the shared {@link pagedInfiniteOptions} apply verbatim.
 *
 * @example
 * ```tsx
 * const feed = useClientNotifications({ isRead: unreadOnly ? false : undefined });
 * const items = flattenClientNotifications(feed.data);
 * ```
 */
export function useClientNotifications(
  options?: UseClientNotificationsOptions
): UseInfiniteQueryResult<InfiniteData<ClientNotificationListResponse>, Error> {
  const isClientSession = useIsClientSession();
  const filter: ClientNotificationFilterRequest = {
    pageSize: CLIENT_NOTIFICATIONS_PAGE_SIZE,
    isRead: options?.isRead,
  };

  return useInfiniteQuery<ClientNotificationListResponse, Error>({
    queryKey: clientKeys.notifications.list(filter),
    queryFn: ({ pageParam }) =>
      clientNotificationsApi.getNotifications({
        ...filter,
        pageNumber: pageParam as number,
      }),
    ...pagedInfiniteOptions,
    enabled: isClientSession && (options?.enabled ?? true),
    staleTime: NOTIFICATIONS_STALE_TIME_MS,
    gcTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// Writes
// ============================================================================

/**
 * Mark one notification read (204, no body).
 *
 * Optimistic: the row loses its unread styling and the badge drops the moment it is tapped,
 * because the tap usually navigates away and a customer would otherwise come back to a screen
 * still claiming the item is new. The counter only moves when the item really was unread.
 */
export function useMarkClientNotificationRead(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, string>({
    mutationFn: (id) => clientNotificationsApi.markRead(id),
    onMutate: (id) => {
      const cached = findCachedNotification(queryClient, id);
      const wasUnread = cached ? !cached.isRead : false;

      patchCachedNotifications(queryClient, (item) =>
        item.id === id ? { ...item, isRead: true } : item
      );
      if (wasUnread) {
        adjustCachedUnreadCount(queryClient, (count) => count - 1);
      }
    },
    // Both on success and on failure: the server is the authority on what is read, and on
    // failure the optimistic patch has to be undone.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: clientKeys.notifications.all });
    },
  });
}

/** Mark every notification read (204, no body). */
export function useMarkAllClientNotificationsRead(): UseMutationResult<void, unknown, void> {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, void>({
    mutationFn: () => clientNotificationsApi.markAllRead(),
    onMutate: () => {
      patchCachedNotifications(queryClient, (item) =>
        item.isRead ? item : { ...item, isRead: true }
      );
      adjustCachedUnreadCount(queryClient, () => 0);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: clientKeys.notifications.all });
    },
  });
}
